import React, { useEffect } from 'react'
import { NameDto } from '@/api/Common/Types'
import { FormField, FormMultiSelectField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { InstrumentDto } from 'api/Trading-Accounts/Types/InstrumentDto'
import {
  Instrument,
  StandardCommission,
} from 'api/Trading-Accounts/Types/IntroducingBrokerDetailedProfileDto'
import { IntroducingBrokerProfileAccountType } from 'api/Trading-Accounts/Types/IntroducingBrokerProfileAccountType'
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'

import { RebateCalculationMethod } from './IntroducingBrokerProfile/types'

interface StandardCommissionFormValues {
  id?: string
  tradingAccountTypes: number[]
  amount: number
  instrumentTypes: number[]
  platformTypes: number[]
  rebateCalculationMethodId: number
  instruments: string[]
  pointValue?: number
}

interface OuterProps {
  introducingBrokerProfileAccountType: IntroducingBrokerProfileAccountType[]

  data?: StandardCommission
  instrumentTypes: NameDto[]
  platformTypes: NameDto[]
  rebateCalcMethods: NameDto[]

  handleChange(values: Partial<StandardCommission>): void
  instruments: Array<Instrument | InstrumentDto>
}

const validationSchema = Yup.object().shape({
  rebateCalculationMethodId: Yup.number()
    .min(1, 'Rebate Calc. Method is required')
    .required('Rebate Calc. Method is required'),
  amount: Yup.number().when('rebateCalculationMethodId', {
    is: (v: number) => v === RebateCalculationMethod.POINT_BASED,
    then: () =>
      Yup.number()
        .min(0.0, 'Commision percentage must be at least 0.0')
        .max(100, 'Commision percentage cannot exceed 100')
        .required('Commision percentage is required'),
    otherwise: () =>
      Yup.number()
        .min(0.0, 'Rebate Amount must be at least 0.0')
        .max(100, 'Rebate Amount cannot exceed 100')
        .required('Rebate Amount is required'),
  }),
  pointValue: Yup.number().when('rebateCalculationMethodId', {
    is: (v: number) => v === RebateCalculationMethod.POINT_BASED,
    then: () =>
      Yup.number().min(0, 'Point Value must be positive').required('Point Value is required'),
    otherwise: () => Yup.number().notRequired(),
  }),
})

export const CommissionModal: React.FC<OuterProps> = ({
  introducingBrokerProfileAccountType,
  handleChange,
  data,
  instrumentTypes,
  platformTypes,
  rebateCalcMethods,
  instruments,
}) => {
  const initialValues: StandardCommissionFormValues = {
    tradingAccountTypes: data?.tradingAccountTypes?.map((ta) => ta.id) || [],
    amount: data?.amount || 0,
    instrumentTypes: data?.instrumentTypes?.map((tt) => tt.id) || [],
    platformTypes: data?.platformTypes?.map((pt) => pt.id) || [],
    rebateCalculationMethodId: data?.rebateCalculationMethod?.id || 0,
    instruments: data?.instruments?.map((instrument) => instrument.id) || [],
    pointValue: data?.pointValue,
  }

  const onSubmit = async (
    values: StandardCommissionFormValues,
    { setSubmitting }: FormikHelpers<StandardCommissionFormValues>
  ) => {
    handleChange(dataToSubmit(values))
    setSubmitting(false)
  }
  const formik = useFormik<StandardCommissionFormValues>({
    initialValues,
    onSubmit,
    validationSchema,
  })
  const calculationMethodId = Number(formik.values.rebateCalculationMethodId)

  const isEdit = !!data

  const mapAndFilter = <T, U>(array: T[] | undefined, mapFn: (item: T) => U | undefined): U[] => {
    return array?.map(mapFn).filter((item): item is U => item !== undefined) ?? []
  }

  const dataToSubmit = (values: StandardCommissionFormValues): StandardCommission => {
    const tradingAccountTypes = mapAndFilter(values.tradingAccountTypes, (type) =>
      introducingBrokerProfileAccountType.find((tat) => tat.id === type)
    )

    const selectedInstrumentTypes = mapAndFilter(values.instrumentTypes, (type) =>
      instrumentTypes.find((it) => it.id === type)
    )

    const selectedPlatformTypes = mapAndFilter(values.platformTypes, (type) =>
      platformTypes.find((pt) => pt.id === type)
    ).map((pt) => ({
      id: pt?.id,
      name: pt.name,
    }))

    const rebateCalculationMethod = rebateCalcMethods.find(
      (g) => g.id === Number(values.rebateCalculationMethodId)
    )
    const selectedInstruments = mapAndFilter(values.instruments, (type) =>
      instruments.find((instrument) => instrument.id === type)
    )
    const hasRebateAmount =
      Number(rebateCalculationMethod?.id) !== RebateCalculationMethod.PER_SPREAD_SHARE

    return {
      tradingAccountTypes,
      ...(hasRebateAmount ? { amount: values.amount } : {}),
      ...(calculationMethodId === RebateCalculationMethod.POINT_BASED
        ? { pointValue: values.pointValue }
        : {}),
      instrumentTypes: selectedInstrumentTypes,
      platformTypes: selectedPlatformTypes,
      rebateCalculationMethod: rebateCalculationMethod!,
      instruments: selectedInstruments,
    }
  }

  useEffect(() => {
    if (!isEdit) {
      formik.setFieldValue('instrumentId', '')
    }
  }, [formik.values.instrumentTypes]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      Number(formik.values.rebateCalculationMethodId) === RebateCalculationMethod.PER_SPREAD_SHARE
    ) {
      formik.setFieldValue('amount', 0)
    }
  }, [formik.values.rebateCalculationMethodId])

  const allOption: NameDto<number> = { id: -1, name: 'All' }

  const handleSelectChange = (value: number[] | string[] | undefined, name: string) => {
    const { id: allOptionId } = allOption

    // Added this change because of the new type declaration
    if (Array.isArray(value) && value?.some((item) => item === allOptionId)) {
      const selectionLength = value.length
      if (value[selectionLength - 1] === allOptionId) {
        formik.setFieldValue(name, [allOptionId])
      } else {
        formik.setFieldValue(name, [value[selectionLength - 1]])
      }
    } else {
      formik.setFieldValue(name, value ?? [])
    }
  }

  return (
    <FormikProvider value={formik}>
      <DialogContent size='sm' render={<Form />}>
        <DialogHeader>
          <DialogTitle>
            {data
              ? `Edit commission for platform: ${data?.platformTypes
                  ?.map((pt) => pt.name)
                  .join(',')}`
              : 'Add New Commission'}
          </DialogTitle>
        </DialogHeader>
        <FormMultiSelectField
          name='platformTypes'
          label='Platform'
          value={formik.values.platformTypes}
          options={[allOption, ...platformTypes]}
          showSearch
          allowClear
          dataTest='servers-field'
          onChange={(value) => handleSelectChange(value, 'platformTypes')}
        />
        <FormMultiSelectField
          name='instrumentTypes'
          label='Instrument Type'
          value={formik.values.instrumentTypes}
          options={[allOption, ...instrumentTypes]}
          showSearch
          allowClear
          dataTest='servers-field'
          onChange={(value) => handleSelectChange(value, 'instrumentTypes')}
        />
        <FormMultiSelectField
          name='instruments'
          label='Instrument'
          value={formik.values.instruments}
          options={[
            { id: allOption.id, name: allOption.name },
            ...instruments.map((instrument) => ({
              id: instrument.id,
              name: instrument.symbol,
            })),
          ]}
          dataTest='platformTypeId-field'
          onChange={(value) => handleSelectChange(value, 'instruments')}
        />
        <FormMultiSelectField
          name='tradingAccountTypes'
          label='Account Type'
          value={formik.values.tradingAccountTypes}
          options={[allOption, ...introducingBrokerProfileAccountType]}
          dataTest='tradingAccountTypes-field'
          onChange={(value) => handleSelectChange(value, 'tradingAccountTypes')}
        />
        <FormSelectField
          name='rebateCalculationMethodId'
          label='Rebate Calc. Method'
          value={formik.values.rebateCalculationMethodId || undefined}
          options={rebateCalcMethods}
          error={formik.errors.rebateCalculationMethodId}
          onChange={(value) => {
            formik.setFieldValue('rebateCalculationMethodId', Number(value))
            formik.validateField('rebateCalculationMethodId')
          }}
        />
        {calculationMethodId !== RebateCalculationMethod.PER_SPREAD_SHARE &&
          calculationMethodId !== RebateCalculationMethod.POINT_BASED && (
            <FormField
              name='amount'
              label='Rebate Amount'
              type='number'
              min={
                Number(formik.values.rebateCalculationMethodId) ===
                RebateCalculationMethod.PER_PERCENTAGE_0F_BASE_CURRENCY
                  ? 0.01
                  : 1
              }
              max={100}
              value={formik.values.amount}
              error={formik.errors.amount}
              onChange={(event) => formik.setFieldValue('amount', Number(event.target.value))}
            />
          )}

        {calculationMethodId === RebateCalculationMethod.POINT_BASED && (
          <FormField
            name='pointValue'
            label='Point Value'
            type='number'
            min={0}
            value={formik.values.pointValue}
            error={formik.errors.pointValue}
            onChange={(event) => formik.setFieldValue('pointValue', Number(event.target.value))}
          />
        )}

        {calculationMethodId === RebateCalculationMethod.POINT_BASED && (
          <FormField
            name='amount'
            label='Commission (%)'
            type='number'
            min={1}
            max={100}
            value={formik.values.amount}
            error={formik.errors.amount}
            onChange={(event) => formik.setFieldValue('amount', Number(event.target.value))}
          />
        )}

        <DialogFooter>
          <Button
            type='button'
            disabled={!formik.isValid || formik.isSubmitting}
            onClick={() => formik.handleSubmit()}
          >
            {data ? 'Edit' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </FormikProvider>
  )
}
